/**
 * Analytics helper — fires every event to BOTH GA4 (gtag) and Microsoft Clarity,
 * always wrapped in try/catch so analytics can never break the UX flow.
 *
 * GA4 receives the event with all contextual params.
 * Clarity receives the same event name, and each context value is set as a
 * custom tag (clarity("set", key, value)) so sessions can be segmented/filtered
 * by article, hub, category and the CTA that was clicked.
 *
 * Custom events fired across the site:
 *   - phone_click          → any tel: link clicked (delegated listener in App.js)
 *   - email_click          → any mailto: link clicked (delegated listener in App.js)
 *   - whatsapp_click       → any WhatsApp link clicked (delegated listener in App.js)
 *   - contact_form_submit  → contact form submitted successfully
 *   - book_survey_click    → a "Book a Survey" / "Book a Diagnostic Survey" CTA clicked
 *   - generate_lead        → form submission success (GA4 recommended event)
 *   - file_download / select_content / contact_cta_click → see callers
 */

// Keys we mirror into Clarity as custom tags for session segmentation.
const CLARITY_TAG_KEYS = [
  "location",
  "cta",
  "article_slug",
  "article_title",
  "category",
  "category_name",
  "hub",
  "hub_name",
  "page_type",
  "service",
  "form",
];

export function trackEvent(name, params = {}) {
  // GA4
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch {
    // analytics never fails the UX
  }
  // Microsoft Clarity — set context tags, then fire the named event.
  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function") {
      for (const k of CLARITY_TAG_KEYS) {
        const v = params[k];
        if (v != null && v !== "") window.clarity("set", k, String(v));
      }
      window.clarity("event", name);
    }
  } catch {
    // analytics never fails the UX
  }
}

// Convenience wrapper for any booking CTA so every button reports a consistent
// shape: where it was clicked from plus the page context (article/hub/category).
export function trackBookSurvey(location, context = {}) {
  trackEvent("book_survey_click", {
    location,
    link_text: "Book a Survey",
    cta: context.cta || "book_survey",
    ...context,
  });
}

// --- Lead attribution -------------------------------------------------------
// CTAs navigate to /contact, losing their originating context. We persist the
// context here so the Contact form can attribute the resulting enquiry to the
// exact article / hub / CTA that generated it.
const LEAD_KEY = "riq_lead_context";

export function setLeadContext(context = {}) {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      LEAD_KEY,
      JSON.stringify({ ...context, captured_at: new Date().toISOString() })
    );
  } catch {
    // ignore storage failures
  }
}

export function getLeadContext() {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.sessionStorage.getItem(LEAD_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function clearLeadContext() {
  try {
    if (typeof window !== "undefined") window.sessionStorage.removeItem(LEAD_KEY);
  } catch {
    // ignore
  }
}
